/**
 * FIE Trust Header — Apple-level premium header.
 * Large confident number, subtle horizontal chips, primary actions.
 */
import { useState } from "react";
import { useFIE } from "@/data/fie/context";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ShieldAlert, Lock, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
}

interface FIETrustHeaderProps {
  onToggleBreakdown?: () => void;
  onOpenLedger?: () => void;
  breakdownOpen?: boolean;
  onExplainThis?: () => void;
  explainActive?: boolean;
  onLockDistribute?: () => void;
}

export function FIETrustHeader({ onToggleBreakdown, onOpenLedger, breakdownOpen, onExplainThis, explainActive, onLockDistribute }: FIETrustHeaderProps) {
  const {
    visibleForecast: f, periods, selectedPeriodId, setSelectedPeriodId,
    isPeriodLocked, canViewEconomics,
  } = useFIE();

  const locked = isPeriodLocked(selectedPeriodId);
  const primaryValue = canViewEconomics && f.netProfit
    ? { label: "Net Profit", value: f.netProfit.expected, range: f.netProfit }
    : f.revenue
      ? { label: "Revenue", value: f.revenue.expected, range: f.revenue }
      : null;

  return (
    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm -mx-6 px-6 pb-5 pt-1">
      <div className="flex items-end justify-between gap-8">
        {/* Left: Primary number */}
        <div className="flex items-end gap-8">
          {primaryValue && (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground mb-1">
                {primaryValue.label}
              </p>
              <p className="text-[38px] font-bold tracking-tight leading-none text-foreground">
                {formatCurrency(primaryValue.value)}
              </p>
              {primaryValue.range && (
                <p className="text-[11px] text-muted-foreground mt-1.5 tabular-nums">
                  {formatCurrency(primaryValue.range.low)} — {formatCurrency(primaryValue.range.high)}
                </p>
              )}
            </div>
          )}

          {/* Chips — ultra-subtle */}
          <div className="flex items-center gap-1.5 mb-1">
            {/* Verification */}
            <Chip
              variant={f.proof.reproducible ? "success" : f.integrityStatus === "mismatch" ? "danger" : "neutral"}
              icon={f.proof.reproducible ? <ShieldCheck className="h-3 w-3" /> : f.integrityStatus === "mismatch" ? <ShieldAlert className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
              label={f.proof.reproducible ? "Verified" : f.integrityStatus === "mismatch" ? "Mismatch" : "Pending"}
            />

            {locked && (
              <Chip variant="warning" icon={<Lock className="h-3 w-3" />} label="Locked" />
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <span><Chip variant="neutral" label={`Confidence ${f.confidence.score}`} /></span>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-xs">
                {f.confidence.factors.slice(0, 3).map((fct, i) => (
                  <p key={i}>{fct.impact === "positive" ? "✓" : fct.impact === "negative" ? "✗" : "—"} {fct.factor}</p>
                ))}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span><Chip variant="neutral" label={`${f.completeness.overall}% Complete`} /></span>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                <p>Contract: {f.completeness.contractCoverage}%</p>
                <p>Costs: {f.completeness.costCompleteness}%</p>
                <p>Schedule: {f.completeness.scheduleCertainty}%</p>
                <p>Permits: {f.completeness.permitCompleteness}%</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Right: Actions + period */}
        <div className="flex items-center gap-3 mb-1">
          {onToggleBreakdown && (
            <button
              onClick={onToggleBreakdown}
              className={cn(
                "h-9 px-4 rounded-lg text-[13px] font-medium transition-all duration-200",
                breakdownOpen
                  ? "bg-foreground text-background shadow-sm"
                  : "bg-foreground/5 text-foreground hover:bg-foreground/10"
              )}
            >
              {breakdownOpen ? (
                <span className="flex items-center gap-1.5"><ChevronUp className="h-3.5 w-3.5" /> Close</span>
              ) : (
                "See What Makes This Up"
              )}
            </button>
          )}
          {onExplainThis && (
            <button
              onClick={onExplainThis}
              className={cn(
                "h-9 px-4 rounded-lg text-[13px] font-medium transition-all duration-200",
                explainActive
                  ? "bg-foreground text-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Explain This
            </button>
          )}
          {onLockDistribute && (
            <button
              onClick={onLockDistribute}
              className="h-9 px-4 rounded-lg text-[13px] font-medium bg-foreground/5 text-foreground hover:bg-foreground/10 transition-all duration-200 flex items-center gap-1.5"
            >
              <Lock className="h-3.5 w-3.5" /> Lock & Distribute
            </button>
          )}
          {onOpenLedger && (
            <button
              onClick={onOpenLedger}
              className="h-9 px-4 rounded-lg text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Open Ledger
            </button>
          )}
          <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
            <SelectTrigger className="w-40 h-9 text-[13px] border-border/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periods.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}{(p.status === "closed" || p.status === "reconciled") ? " 🔒" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// ── Minimal Status Chip ──
function Chip({ variant, icon, label }: {
  variant: "success" | "danger" | "warning" | "neutral";
  icon?: React.ReactNode;
  label: string;
}) {
  const styles = {
    success: "bg-success/8 text-success",
    danger: "bg-destructive/8 text-destructive",
    warning: "bg-warning/8 text-warning",
    neutral: "bg-muted text-muted-foreground",
  };

  return (
    <span className={cn(
      "inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[11px] font-medium tracking-wide",
      styles[variant]
    )}>
      {icon}
      {label}
    </span>
  );
}
